import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  KeyboardSensor,
  closestCorners,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  horizontalListSortingStrategy,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import { message } from 'antd';
import { tasksApi, taskboardsColumnsApi } from '../../../utils/workApi';
import { TableSkeleton } from '../../SkeletonLoaders';
import TaskKanbanColumn from './TaskKanbanColumn';
import TaskCard from './TaskCard';
import ChaproomPackagingShippingModal from './ChaproomPackagingShippingModal';
import { useAuth } from '../../../contexts/AuthContext';
import { hasCompanyFeature, isChaproomPackagingColumn } from '../../../company/features';

const resolveColumnHasMore = (col, itemsLength) => {
  if (col?.load_more === 'hide' || col?.has_more === false) return false;
  if (col?.load_more === 'show' || col?.has_more === true) return true;
  const total = Number(col?.tasks_count) || 0;
  return total > itemsLength;
};

const bumpColumnCount = (col, delta) => {
  if (!col) return col;
  const current = Number(col.tasks_count);
  const base = Number.isFinite(current) ? current : 0;
  return { ...col, tasks_count: Math.max(0, base + delta) };
};

const TaskKanbanBoard = ({
  filters = {},
  onTaskClick,
  active = true,
  cachedBoard = null,
  cachedBoardKey = '',
  onBoardCached,
  onFiltersFromResponse,
}) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const boardScrollRef = useRef(null);
  const dragPointerRef = useRef({ x: null, y: null });
  const autoScrollFrameRef = useRef(null);
  const filtersRef = useRef(filters);
  const lastFetchedKeyRef = useRef('');
  const fetchInFlightRef = useRef(false);
  const onBoardCachedRef = useRef(onBoardCached);
  const onFiltersFromResponseRef = useRef(onFiltersFromResponse);
  const [loading, setLoading] = useState(true);
  const [columns, setColumns] = useState([]);
  const [tasksByColumn, setTasksByColumn] = useState({});
  const [columnHasMore, setColumnHasMore] = useState({});
  const [loadingMoreColumnId, setLoadingMoreColumnId] = useState(null);
  const [activeTask, setActiveTask] = useState(null);
  const [packagingModalOpen, setPackagingModalOpen] = useState(false);
  const [packagingSaving, setPackagingSaving] = useState(false);
  const [pendingPackagingMove, setPendingPackagingMove] = useState(null);
  const [activeColumn, setActiveColumn] = useState(null);

  filtersRef.current = filters;
  onBoardCachedRef.current = onBoardCached;
  onFiltersFromResponseRef.current = onFiltersFromResponse;

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 3,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const filterKey = JSON.stringify(filters);

  const stopAutoScroll = useCallback(() => {
    if (autoScrollFrameRef.current) {
      cancelAnimationFrame(autoScrollFrameRef.current);
      autoScrollFrameRef.current = null;
    }
    dragPointerRef.current = { x: null, y: null };
  }, []);

  const runHorizontalAutoScroll = useCallback(() => {
    const container = boardScrollRef.current;
    const pointerX = dragPointerRef.current.x;
    if (!container || pointerX == null) {
      autoScrollFrameRef.current = null;
      return;
    }

    const rect = container.getBoundingClientRect();
    const edgeSize = 96;
    const maxSpeed = 28;
    let delta = 0;

    if (pointerX < rect.left + edgeSize) {
      const ratio = Math.min(1, Math.max(0, (rect.left + edgeSize - pointerX) / edgeSize));
      delta = -Math.ceil(maxSpeed * ratio);
    } else if (pointerX > rect.right - edgeSize) {
      const ratio = Math.min(1, Math.max(0, (pointerX - (rect.right - edgeSize)) / edgeSize));
      delta = Math.ceil(maxSpeed * ratio);
    }

    if (delta !== 0) {
      container.scrollLeft += delta;
    }

    autoScrollFrameRef.current = requestAnimationFrame(runHorizontalAutoScroll);
  }, []);

  const handleDragMove = useCallback(
    (event) => {
      const activatorEvent = event.activatorEvent;
      const pointerEvent = event?.activatorEvent?.touches?.[0] || activatorEvent;
      if (pointerEvent?.clientX != null) {
        dragPointerRef.current = { x: pointerEvent.clientX, y: pointerEvent.clientY };
      }
      if (!autoScrollFrameRef.current) {
        autoScrollFrameRef.current = requestAnimationFrame(runHorizontalAutoScroll);
      }
    },
    [runHorizontalAutoScroll],
  );

  useEffect(() => {
    if (!activeTask && !activeColumn) {
      stopAutoScroll();
      return undefined;
    }

    const handlePointerMove = (event) => {
      dragPointerRef.current = { x: event.clientX, y: event.clientY };
      if (!autoScrollFrameRef.current) {
        autoScrollFrameRef.current = requestAnimationFrame(runHorizontalAutoScroll);
      }
    };
    const handleTouchMove = (event) => {
      const touch = event.touches?.[0];
      if (!touch) return;
      dragPointerRef.current = { x: touch.clientX, y: touch.clientY };
      if (!autoScrollFrameRef.current) {
        autoScrollFrameRef.current = requestAnimationFrame(runHorizontalAutoScroll);
      }
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('touchmove', handleTouchMove, { passive: true });

    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('touchmove', handleTouchMove);
      stopAutoScroll();
    };
  }, [activeTask, activeColumn, runHorizontalAutoScroll, stopAutoScroll]);

  const fetchBoardData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await tasksApi.getBoard(filtersRef.current);
      const cols = res?.columns || res?.boardColumns || res?.taskboard_columns || [];
      const taskList = res?.tasks?.data || res?.tasks || [];
      const grouped = {};
      const hasMoreByColumn = {};
      cols.forEach((c) => {
        const key = String(c.id);
        grouped[key] = [];
        hasMoreByColumn[key] = false;
      });
      (Array.isArray(taskList) ? taskList : []).forEach((t) => {
        const key = String(t.board_column_id);
        if (!grouped[key]) grouped[key] = [];
        grouped[key].push(t);
      });
      cols.forEach((c) => {
        const key = String(c.id);
        hasMoreByColumn[key] = resolveColumnHasMore(c, (grouped[key] || []).length);
      });
      setColumns(cols);
      setTasksByColumn(grouped);
      setColumnHasMore(hasMoreByColumn);

      const boardFilters = res?.data?.filters || res?.filters;
      if (boardFilters) {
        onFiltersFromResponseRef.current?.(boardFilters);
      }

      onBoardCachedRef.current?.({
        key: filterKey,
        columns: cols,
        tasks: grouped,
        columnHasMore: hasMoreByColumn,
      });
      lastFetchedKeyRef.current = filterKey;
    } catch {
      message.error('خطا در بارگذاری برد وظایف');
    } finally {
      setLoading(false);
      fetchInFlightRef.current = false;
    }
  }, [filterKey]);

  useEffect(() => {
    if (!active) return;
    if (lastFetchedKeyRef.current === filterKey) return;

    if (cachedBoardKey === filterKey && cachedBoard?.columns?.length) {
      setColumns(cachedBoard.columns);
      setTasksByColumn(cachedBoard.tasks || {});
      setColumnHasMore(cachedBoard.columnHasMore || {});
      setLoading(false);
      lastFetchedKeyRef.current = filterKey;
      return;
    }

    if (fetchInFlightRef.current) return;

    const timer = setTimeout(() => {
      if (fetchInFlightRef.current) return;
      fetchInFlightRef.current = true;
      fetchBoardData();
    }, 450);
    return () => clearTimeout(timer);
  }, [active, filterKey, cachedBoardKey, cachedBoard, fetchBoardData]);

  const loadBoard = useCallback(() => {
    lastFetchedKeyRef.current = '';
    fetchInFlightRef.current = true;
    fetchBoardData();
  }, [fetchBoardData]);

  const handleLoadMore = useCallback(async (columnId) => {
    const colKey = String(columnId);
    const currentItems = tasksByColumn[colKey] || [];
    setLoadingMoreColumnId(colKey);
    try {
      const res = await tasksApi.loadMoreBoard({
        ...filtersRef.current,
        column_id: colKey,
        currentTotalTasks: currentItems.length,
      });
      const rawItems = res?.tasks?.data || res?.tasks || [];
      const newItems = Array.isArray(rawItems) ? rawItems : [];
      const existingIds = new Set(currentItems.map((t) => t.id));
      const merged = [
        ...currentItems,
        ...newItems.filter((t) => t?.id != null && !existingIds.has(t.id)),
      ];

      setTasksByColumn((prev) => ({ ...prev, [colKey]: merged }));

      const col = columns.find((c) => String(c.id) === colKey);
      const total = Number(col?.tasks_count);
      const hasMore =
        res?.load_more === 'show' ||
        res?.has_more === true ||
        (Number.isFinite(total) && merged.length < total);

      setColumnHasMore((prev) => ({
        ...prev,
        [colKey]: res?.load_more === 'hide' ? false : hasMore,
      }));
    } catch {
      message.error('خطا در بارگذاری موارد بیشتر');
    } finally {
      setLoadingMoreColumnId(null);
    }
  }, [tasksByColumn, columns]);

  const scrollBoard = (delta) => {
    boardScrollRef.current?.scrollBy({ left: delta, behavior: 'smooth' });
  };

  const handleDragStart = (event) => {
    const id = String(event.active.id);
    if (id.startsWith('task-')) {
      const taskId = parseInt(id.replace('task-', ''), 10);
      Object.values(tasksByColumn).forEach((list) => {
        const t = list.find((x) => x.id === taskId);
        if (t) setActiveTask(t);
      });
      return;
    }
    if (id.startsWith('column-')) {
      const colId = id.replace('column-', '');
      const col = columns.find((c) => String(c.id) === String(colId));
      if (col) setActiveColumn(col);
    }
  };

  const persistColumnOrder = async (orderedCols) => {
    try {
      await Promise.all(
        orderedCols.map((col, index) =>
          taskboardsColumnsApi.update(col.id, {
            column_name: col.column_name,
            label_color: col.label_color || col.color,
            priority: index + 1,
          }),
        ),
      );
    } catch {
      message.error('خطا در ذخیره ترتیب ستون‌ها');
      loadBoard();
    }
  };

  const applyTaskColumnMove = async ({
    taskId,
    sourceColId,
    targetColId,
    task,
    statusSlug,
    shippingExtra = {},
  }) => {
    setTasksByColumn((prev) => {
      const next = { ...prev };
      next[sourceColId] = (next[sourceColId] || []).filter((t) => t.id !== taskId);
      next[targetColId] = [
        ...(next[targetColId] || []),
        { ...task, board_column_id: Number(targetColId) },
      ];
      return next;
    });

    setColumns((prev) =>
      prev.map((c) => {
        const key = String(c.id);
        if (key === String(sourceColId)) return bumpColumnCount(c, -1);
        if (key === String(targetColId)) return bumpColumnCount(c, 1);
        return c;
      }),
    );

    setColumnHasMore((prev) => {
      const next = { ...prev };
      const sourceLen = ((tasksByColumn[sourceColId] || []).length) - 1;
      const targetLen = ((tasksByColumn[targetColId] || []).length) + 1;
      const sourceCol = columns.find((c) => String(c.id) === String(sourceColId));
      const destCol = columns.find((c) => String(c.id) === String(targetColId));
      next[sourceColId] = resolveColumnHasMore(
        bumpColumnCount(sourceCol, -1),
        Math.max(0, sourceLen),
      );
      next[targetColId] = resolveColumnHasMore(bumpColumnCount(destCol, 1), targetLen);
      return next;
    });

    try {
      await tasksApi.changeStatus(taskId, statusSlug, shippingExtra);
      return true;
    } catch {
      message.error('خطا در تغییر وضعیت');
      loadBoard();
      return false;
    }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    setActiveTask(null);
    setActiveColumn(null);
    stopAutoScroll();
    if (!over) return;

    const activeId = String(active.id);
    const overId = String(over.id);

    if (activeId.startsWith('column-') && overId.startsWith('column-')) {
      const activeIndex = columns.findIndex((col) => `column-${col.id}` === activeId);
      const overIndex = columns.findIndex((col) => `column-${col.id}` === overId);
      if (activeIndex !== -1 && overIndex !== -1 && activeIndex !== overIndex) {
        const next = arrayMove(columns, activeIndex, overIndex);
        setColumns(next);
        await persistColumnOrder(next);
      }
      return;
    }

    if (!activeId.startsWith('task-')) return;

    const taskId = parseInt(activeId.replace('task-', ''), 10);
    let targetColId = null;
    if (overId.startsWith('column-')) {
      targetColId = overId.replace('column-', '');
    } else if (overId.startsWith('task-')) {
      const overTaskId = parseInt(overId.replace('task-', ''), 10);
      Object.entries(tasksByColumn).forEach(([colId, list]) => {
        if (list.some((t) => t.id === overTaskId)) targetColId = colId;
      });
    }
    if (!targetColId) return;

    let sourceColId = null;
    let task = null;
    Object.entries(tasksByColumn).forEach(([colId, list]) => {
      const found = list.find((t) => t.id === taskId);
      if (found) {
        sourceColId = colId;
        task = found;
      }
    });
    if (!task || sourceColId === targetColId) return;

    const targetCol = columns.find((c) => String(c.id) === String(targetColId));
    const statusSlug = targetCol?.slug || targetColId;

    const needsShippingModal =
      hasCompanyFeature(user?.company_id, 'task.chaproomPackagingShipping') &&
      isChaproomPackagingColumn(targetCol);

    if (needsShippingModal) {
      setPendingPackagingMove({
        taskId,
        sourceColId,
        targetColId,
        task,
        statusSlug,
      });
      setPackagingModalOpen(true);
      return;
    }

    await applyTaskColumnMove({
      taskId,
      sourceColId,
      targetColId,
      task,
      statusSlug,
    });
  };

  const handlePackagingModalSubmit = async (values) => {
    if (!pendingPackagingMove) return;
    setPackagingSaving(true);
    try {
      const ok = await applyTaskColumnMove({
        ...pendingPackagingMove,
        shippingExtra: {
          trackingCode: values.trackingCode,
          postageMethod: values.postageMethod,
        },
      });
      if (ok) {
        setPackagingModalOpen(false);
        setPendingPackagingMove(null);
      }
    } finally {
      setPackagingSaving(false);
    }
  };

  const handlePackagingModalCancel = () => {
    if (packagingSaving) return;
    setPackagingModalOpen(false);
    setPendingPackagingMove(null);
  };

  if (loading) {
    return <TableSkeleton rows={4} columns={4} />;
  }

  return (
    <div style={{ fontFamily: 'CustomFont' }}>
      <div className="relative">
        <button
          type="button"
          onClick={() => scrollBoard(320)}
          className="absolute right-2 top-1/2 -translate-y-1/2 z-30 w-10 h-10 rounded-full bg-white/95 dark:bg-gray-800/95 border border-gray-200 dark:border-gray-600 shadow-lg flex items-center justify-center text-gray-600 dark:text-gray-200 hover:bg-indigo-50 hover:text-indigo-600 hover:border-indigo-200 transition-all opacity-90 hover:opacity-100"
          aria-label="اسکرول به راست"
        >
          <RightOutlined />
        </button>
        <button
          type="button"
          onClick={() => scrollBoard(-320)}
          className="absolute left-2 top-1/2 -translate-y-1/2 z-30 w-10 h-10 rounded-full bg-white/95 dark:bg-gray-800/95 border border-gray-200 dark:border-gray-600 shadow-lg flex items-center justify-center text-gray-600 dark:text-gray-200 hover:bg-indigo-50 hover:text-indigo-600 hover:border-indigo-200 transition-all opacity-90 hover:opacity-100"
          aria-label="اسکرول به چپ"
        >
          <LeftOutlined />
        </button>

        <div
          ref={boardScrollRef}
          className="w-full overflow-x-auto kanban-scrollbar kanban-board-container"
          style={{
            overflowY: 'hidden',
            maxHeight: 'calc(100vh - 350px)',
            height: 'calc(100vh - 350px)',
            minHeight: '600px',
          }}
        >
          <DndContext
            sensors={sensors}
            collisionDetection={closestCorners}
            onDragStart={handleDragStart}
            onDragMove={handleDragMove}
            onDragEnd={handleDragEnd}
            onDragCancel={() => {
              setActiveTask(null);
              setActiveColumn(null);
              stopAutoScroll();
            }}
          >
            <div
              className="flex gap-4 pb-4 h-full px-2"
              style={{
                minWidth: 'max-content',
                alignItems: 'flex-start',
                WebkitOverflowScrolling: 'touch',
              }}
            >
              <SortableContext
                items={columns.map((c) => `column-${c.id}`)}
                strategy={horizontalListSortingStrategy}
              >
                {columns.map((col) => (
                  <TaskKanbanColumn
                    key={col.id}
                    column={col}
                    tasks={tasksByColumn[String(col.id)] || []}
                    onTaskClick={onTaskClick}
                    onAddTask={(columnId) =>
                      navigate(`/work/tasks/add?board_column_id=${columnId}`)
                    }
                    hasMore={Boolean(columnHasMore[String(col.id)])}
                    loadingMore={loadingMoreColumnId === String(col.id)}
                    onLoadMore={() => handleLoadMore(col.id)}
                  />
                ))}
              </SortableContext>
            </div>
            <DragOverlay>
              {activeTask ? (
                <div className="w-[280px] rotate-2 opacity-95">
                  <TaskCard task={activeTask} overlay />
                </div>
              ) : activeColumn ? (
                <div
                  className="w-80 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-xl p-4"
                  style={{
                    borderTop: `3px solid ${activeColumn.label_color || activeColumn.color || '#6366f1'}`,
                    fontFamily: 'CustomFont',
                  }}
                >
                  <div className="font-bold text-gray-800 dark:text-gray-100">
                    {activeColumn.column_name || activeColumn.title}
                  </div>
                </div>
              ) : null}
            </DragOverlay>
          </DndContext>
        </div>
      </div>
      <ChaproomPackagingShippingModal
        open={packagingModalOpen}
        confirmLoading={packagingSaving}
        onCancel={handlePackagingModalCancel}
        onSubmit={handlePackagingModalSubmit}
      />
    </div>
  );
};

export default TaskKanbanBoard;
